import { trigOfDegrees } from "@/lib/tool-kit/linear-algebra/exact-angle";
import {
  addQ,
  isRationalQ,
  mulQ,
  negQ,
  type QuadNumber,
  quadFromRational,
  quadToComplex,
  scaleQ,
} from "@/lib/tool-kit/linear-algebra/quad-number";
import {
  addR,
  divR,
  intR,
  mulR,
  negR,
  RATIONAL_ZERO,
  type Rational,
  subR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * Translations, reflections, rotations and dilations of points in the plane, as 3 × 3
 * homogeneous matrices.
 *
 * Every step is computed twice: exactly, as numbers a + b√d (so a 45° rotation has
 * entries √2/2), and in floating point for the plot. The exact computation gives up —
 * returns null — only when two different radicals would have to be combined, as in a 30°
 * rotation followed by a 45° one; the page then shows decimals instead.
 */

export interface Point {
  x: Rational;
  y: Rational;
}

export type ReflectionLine =
  | { kind: "x-axis" }
  | { kind: "y-axis" }
  | { kind: "origin" }
  | { kind: "y=x" }
  | { kind: "y=-x" }
  /** y = m x + b */
  | { kind: "slope"; m: Rational; b: Rational }
  /** x = c */
  | { kind: "vertical"; c: Rational };

export type Step =
  | { type: "translate"; dx: Rational; dy: Rational }
  | { type: "reflect"; line: ReflectionLine }
  | {
      type: "rotate";
      /** Counterclockwise angle in degrees; negative is clockwise. */
      degrees: number;
      exactDegrees: number | null;
      center: Point;
    }
  | { type: "dilate"; k: Rational; center: Point };

export type ExactMatrix = QuadNumber[][];
export type NumericMatrix = number[][];

export interface StepMatrix {
  exact: ExactMatrix | null;
  numeric: NumericMatrix;
}

const Q = quadFromRational;
const ZERO = Q(RATIONAL_ZERO);
const ONE = Q(intR(1));

/* ----------------------------------------------- exact arithmetic that can fail */

function tryAdd(a: QuadNumber, b: QuadNumber): QuadNumber | null {
  if (isRationalQ(a) || isRationalQ(b) || a.d === b.d) {
    return addQ(a, b);
  }
  return null;
}

function tryMul(a: QuadNumber, b: QuadNumber): QuadNumber | null {
  if (isRationalQ(a) || isRationalQ(b) || a.d === b.d) {
    return mulQ(a, b);
  }
  return null;
}

function tryDot(row: QuadNumber[], column: QuadNumber[]): QuadNumber | null {
  let sum = ZERO;
  for (let k = 0; k < row.length; k += 1) {
    const product = tryMul(row[k], column[k]);
    const next = product ? tryAdd(sum, product) : null;
    if (next === null) {
      return null;
    }
    sum = next;
  }
  return sum;
}

function exactProduct(a: ExactMatrix, b: ExactMatrix): ExactMatrix | null {
  const result: ExactMatrix = [];
  for (let i = 0; i < 3; i += 1) {
    const row: QuadNumber[] = [];
    for (let j = 0; j < 3; j += 1) {
      const entry = tryDot(a[i], [b[0][j], b[1][j], b[2][j]]);
      if (entry === null) {
        return null;
      }
      row.push(entry);
    }
    result.push(row);
  }
  return result;
}

function numericProduct(a: NumericMatrix, b: NumericMatrix): NumericMatrix {
  return a.map((row) =>
    [0, 1, 2].map((j) => row[0] * b[0][j] + row[1] * b[1][j] + row[2] * b[2][j])
  );
}

/** Multiply two step matrices: `second` · `first`, so `first` acts on a point first. */
export function composeMatrices(
  second: StepMatrix,
  first: StepMatrix
): StepMatrix {
  const exact =
    second.exact && first.exact
      ? exactProduct(second.exact, first.exact)
      : null;
  return { exact, numeric: numericProduct(second.numeric, first.numeric) };
}

/* ------------------------------------------------------------ building steps */

function rational3(rows: Rational[][]): StepMatrix {
  return {
    exact: rows.map((r) => r.map(Q)),
    numeric: rows.map((r) => r.map(toNumberR)),
  };
}

const I3 = rational3([
  [intR(1), RATIONAL_ZERO, RATIONAL_ZERO],
  [RATIONAL_ZERO, intR(1), RATIONAL_ZERO],
  [RATIONAL_ZERO, RATIONAL_ZERO, intR(1)],
]);

function translation(dx: Rational, dy: Rational): StepMatrix {
  return rational3([
    [intR(1), RATIONAL_ZERO, dx],
    [RATIONAL_ZERO, intR(1), dy],
    [RATIONAL_ZERO, RATIONAL_ZERO, intR(1)],
  ]);
}

/** T(c) · M · T(−c): apply the linear step M about the point c. */
function about(center: Point, linear: StepMatrix): StepMatrix {
  const there = translation(negR(center.x), negR(center.y));
  const back = translation(center.x, center.y);
  return composeMatrices(back, composeMatrices(linear, there));
}

function linear2(
  a: Rational,
  b: Rational,
  c: Rational,
  d: Rational
): StepMatrix {
  return rational3([
    [a, b, RATIONAL_ZERO],
    [c, d, RATIONAL_ZERO],
    [RATIONAL_ZERO, RATIONAL_ZERO, intR(1)],
  ]);
}

function reflectionMatrix(line: ReflectionLine): StepMatrix {
  const one = intR(1);
  const minusOne = intR(-1);
  const zero = RATIONAL_ZERO;
  switch (line.kind) {
    case "x-axis":
      return linear2(one, zero, zero, minusOne);
    case "y-axis":
      return linear2(minusOne, zero, zero, one);
    case "origin":
      return linear2(minusOne, zero, zero, minusOne);
    case "y=x":
      return linear2(zero, one, one, zero);
    case "y=-x":
      return linear2(zero, minusOne, minusOne, zero);
    case "vertical":
      return about({ x: line.c, y: zero }, linear2(minusOne, zero, zero, one));
    default: {
      // Reflection across y = m x: [[1 − m², 2m], [2m, m² − 1]] / (1 + m²).
      const m2 = mulR(line.m, line.m);
      const denominator = addR(one, m2);
      const cos2 = divR(subR(one, m2), denominator);
      const sin2 = divR(mulR(intR(2), line.m), denominator);
      return about(
        { x: zero, y: line.b },
        linear2(cos2, sin2, sin2, negR(cos2))
      );
    }
  }
}

function rotationMatrix(
  degrees: number,
  exactDegrees: number | null
): StepMatrix {
  const radians = (degrees * Math.PI) / 180;
  const c = Math.cos(radians);
  const s = Math.sin(radians);
  const numeric: NumericMatrix = [
    [c, -s, 0],
    [s, c, 0],
    [0, 0, 1],
  ];
  const trig = exactDegrees === null ? null : trigOfDegrees(exactDegrees);
  if (!trig) {
    return { exact: null, numeric };
  }
  return {
    exact: [
      [trig.cos, negQ(trig.sin), ZERO],
      [trig.sin, trig.cos, ZERO],
      [ZERO, ZERO, ONE],
    ],
    numeric,
  };
}

export function stepMatrix(step: Step): StepMatrix {
  switch (step.type) {
    case "translate":
      return translation(step.dx, step.dy);
    case "reflect":
      return reflectionMatrix(step.line);
    case "rotate":
      return about(
        step.center,
        rotationMatrix(step.degrees, step.exactDegrees)
      );
    default: {
      const zero = RATIONAL_ZERO;
      return about(step.center, linear2(step.k, zero, zero, step.k));
    }
  }
}

/** The matrix of doing every step in order. */
export function composeSteps(steps: Step[]): StepMatrix {
  return steps.reduce(
    (total, step) => composeMatrices(stepMatrix(step), total),
    I3
  );
}

/* ------------------------------------------------------------------- images */

export interface Coord {
  exact: QuadNumber | null;
  value: number;
}

export interface Image {
  x: Coord;
  y: Coord;
}

function coordOf(exact: QuadNumber | null, value: number): Coord {
  return { exact, value: exact ? quadToComplex(exact).re : value };
}

/** Apply a matrix to a point. */
export function applyMatrix(m: StepMatrix, p: Point): Image {
  const xn = toNumberR(p.x);
  const yn = toNumberR(p.y);
  const num = (row: number[]) => row[0] * xn + row[1] * yn + row[2];
  let ex: QuadNumber | null = null;
  let ey: QuadNumber | null = null;
  if (m.exact) {
    ex = tryDot(m.exact[0], [Q(p.x), Q(p.y), ONE]);
    ey = tryDot(m.exact[1], [Q(p.x), Q(p.y), ONE]);
  }
  const exact = ex && ey;
  return {
    x: coordOf(exact ? ex : null, num(m.numeric[0])),
    y: coordOf(exact ? ey : null, num(m.numeric[1])),
  };
}

/** The 2 × 2 linear part's determinant: the signed factor by which areas scale. */
export function linearDeterminant(m: StepMatrix): Coord {
  const numeric =
    m.numeric[0][0] * m.numeric[1][1] - m.numeric[0][1] * m.numeric[1][0];
  if (!m.exact) {
    return { exact: null, value: numeric };
  }
  const a = tryMul(m.exact[0][0], m.exact[1][1]);
  const b = tryMul(m.exact[0][1], m.exact[1][0]);
  const exact = a && b ? tryAdd(a, negQ(b)) : null;
  return coordOf(exact, numeric);
}

/** The area of a polygon by the shoelace formula (absolute value). */
export function polygonArea(points: Point[]): Rational {
  let twice = RATIONAL_ZERO;
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i];
    const q = points[(i + 1) % points.length];
    twice = addR(twice, subR(mulR(p.x, q.y), mulR(q.x, p.y)));
  }
  const half = divR(twice, intR(2));
  return half.num < BigInt(0) ? negR(half) : half;
}

export function imageArea(images: Image[]): Coord | null {
  if (images.length < 3) {
    return null;
  }
  const exactPoints = images.every((p) => p.x.exact && p.y.exact);
  const numeric = shoelace(images.map((p) => [p.x.value, p.y.value]));
  if (!exactPoints) {
    return { exact: null, value: numeric };
  }
  let twice: QuadNumber | null = ZERO;
  for (let i = 0; i < images.length && twice; i += 1) {
    const p = images[i];
    const q = images[(i + 1) % images.length];
    const a = tryMul(p.x.exact as QuadNumber, q.y.exact as QuadNumber);
    const b = tryMul(q.x.exact as QuadNumber, p.y.exact as QuadNumber);
    const term: QuadNumber | null = a && b ? tryAdd(a, negQ(b)) : null;
    twice = term ? tryAdd(twice, term) : null;
  }
  if (twice === null) {
    return { exact: null, value: numeric };
  }
  let half = scaleQ(twice, divR(intR(1), intR(2)));
  if (quadToComplex(half).re < 0) {
    half = negQ(half);
  }
  return coordOf(half, numeric);
}

function shoelace(points: number[][]): number {
  let twice = 0;
  for (let i = 0; i < points.length; i += 1) {
    const [px, py] = points[i];
    const [qx, qy] = points[(i + 1) % points.length];
    twice += px * qy - qx * py;
  }
  return Math.abs(twice) / 2;
}
